import { useMemo, useState } from 'react';

import { Bar, Empty, Search } from '../../components/ui/bits.jsx';
import { useUI } from '../../components/ui/feedback.jsx';
import { useDebounced } from '../../hooks.js';
import { useSola } from '../../sola.jsx';
import { matchStudent, totalSubConfirmed, totalSubs } from '../../lib/progress.js';
import { downloadCSV } from '../../lib/csv.js';
import { tecajniki } from '../../lib/format.js';
import { Icon } from '../../components/ui/Icon.jsx';
import { t } from '../../i18n/index.js';

/*
  Seznam tečajnikov.

  Novo proti prejšnji verziji: filter „samo čakajoči“. Ko se inštruktor
  vrne s terena, ga zanima točno to — kdo čaka na potrditev — ne pa
  brskanje po celem seznamu.
*/

export function StudentList({ users, summary, selected, onSelect, progress }) {
  const { toast } = useUI();
  const { program } = useSola();
  const TOTAL = totalSubs(program.all);
  const [filter, setFilter] = useState('active');
  const [onlyPending, setOnlyPending] = useState(false);
  const [query, setQuery] = useState('');
  const q = useDebounced(query, 180);

  const counts = useMemo(
    () => ({
      active: users.filter((u) => !u.archived).length,
      archived: users.filter((u) => u.archived).length,
    }),
    [users],
  );

  const list = useMemo(
    () =>
      users
        .filter((u) => (filter === 'archived' ? u.archived : !u.archived))
        .filter((u) => !onlyPending || (summary.perUser[u.id]?.pending ?? 0) > 0)
        .filter((u) => matchStudent(u, q)),
    [users, filter, onlyPending, q, summary],
  );

  return (
    <div className="card admin-list" style={{ position: 'sticky', top: 'var(--s-4)' }}>
      <div className="tabs" role="tablist" aria-label={t('Filter seznama')} style={{ margin: 'var(--s-2)' }}>
        {[
          ['active', t('Aktivni'), counts.active],
          ['archived', t('Arhiv'), counts.archived],
        ].map(([v, label, n]) => (
          <button
            key={v}
            className="tab"
            role="tab"
            aria-selected={filter === v}
            onClick={() => {
              setFilter(v);
              onSelect(null);
            }}
          >
            {label} <span className="num t-dimmer">({n})</span>
          </button>
        ))}
      </div>

      <div className="row" style={{ padding: '0 var(--s-2) var(--s-2)' }}>
        <Search value={query} onChange={setQuery} placeholder={t('Številka ali ime')} />
        <button
          className="btn btn--sm btn--secondary"
          title={t('Izvozi oštevilčene tečajnike v CSV za Excel')}
          onClick={() => {
            const n = downloadCSV(users);
            toast.ok(t('Izvoženih {n} {w}.', { n, w: tecajniki(n) }));
          }}
        >
          <Icon name="download" size={15} /> CSV
        </button>
      </div>

      <label
        className="row t-sm"
        style={{ padding: '0 var(--s-4) var(--s-3)', cursor: 'pointer' }}
      >
        <input
          type="checkbox"
          className="checkbox"
          checked={onlyPending}
          onChange={(e) => setOnlyPending(e.target.checked)}
        />
        <span className="spacer">{t('Samo tisti, ki čakajo potrditev')}</span>
        {summary.pending > 0 && <span className="badge badge--solid num">{summary.pending}</span>}
      </label>

      <div className="list" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
        {list.map((u) => (
          <Row
            key={u.id}
            u={u}
            stats={summary.perUser[u.id]}
            confirmed={totalSubConfirmed(program.all, progress?.[u.id] ?? {})}
            total={TOTAL}
            active={selected === u.id}
            onSelect={() => onSelect(u.id)}
          />
        ))}

        {list.length === 0 && (
          <Empty
            icon="search"
            title={q ? t('Ni zadetkov') : onlyPending ? t('Nič ne čaka') : t('Seznam je prazen')}
            hint={
              q
                ? t('Poskusi drugo številko ali ime.')
                : onlyPending
                  ? t('Vse je potrjeno.')
                  : undefined
            }
          />
        )}
      </div>
    </div>
  );
}

function Row({ u, stats, confirmed, total: TOTAL, active, onSelect }) {
  const name = `${u.ime ?? ''} ${u.priimek ?? ''}`.trim();

  return (
    <button
      className="list__row"
      aria-current={active ? 'true' : undefined}
      onClick={onSelect}
      style={{ flexDirection: 'column', alignItems: 'stretch', gap: 'var(--s-1)' }}
    >
      <span className="row" style={{ gap: 'var(--s-2)' }}>
        <span
          className={`num t-sm t-black ${u.stevilka != null ? 't-brand' : 't-dimmer'}`}
          style={{ minWidth: 30 }}
        >
          {u.stevilka != null ? `#${u.stevilka}` : '—'}
        </span>

        <span className="t-md t-bold truncate spacer" style={{ textAlign: 'left' }}>
          {name || <em className="t-dimmer">{t('Ni izpolnil podatkov')}</em>}
        </span>

        {u.testni && <span className="badge badge--mute">{t('TEST')}</span>}
        {u.archived && <span className="badge badge--mute">{t('ARHIV')}</span>}
        {!u.testni && !u.archived && stats?.readyExDocs && (
          <Icon
            name="award"
            size={16}
            label={t('Pripravljen na izpit')}
            className="t-ok"
          />
        )}
        {!u.placano && !u.archived && (
          <Icon name="euro" size={16} label={t('Ni plačano')} className="t-bad" />
        )}
        {u.admin_notes && (
          <Icon name="note" size={16} label={t('Ima opombo inštruktorja')} className="t-dimmer" />
        )}
        {stats?.pending > 0 && !u.archived && (
          <span className="badge badge--solid num">{stats.pending}</span>
        )}
      </span>

      <span className="t-xs t-dimmer truncate" style={{ textAlign: 'left' }}>
        {u.email}
      </span>

      <span className="row" style={{ gap: 'var(--s-2)' }}>
        <Bar done={confirmed} total={TOTAL} label={t('{name}: {a} od {b}', { name, a: confirmed, b: TOTAL })} />
        <span className="t-xs t-brand t-bold num">
          {confirmed}/{TOTAL}
        </span>
      </span>
    </button>
  );
}
